import { useState, useEffect } from "react";
import Navbar from "@/components/landing/Navbar";
import HeroSection from "@/components/landing/HeroSection";
import ProblemSection from "@/components/landing/ProblemSection";
import SolutionSection from "@/components/landing/SolutionSection";
import TheDifference from "@/components/landing/TheDifference";
import EarlyAccessSection from "@/components/landing/EarlyAccessSection";
import AIPreview from "@/components/landing/AIPreview";
import SocialProof from "@/components/landing/SocialProof";
import FAQ from "@/components/landing/FAQ";
import FinalCTA from "@/components/landing/FinalCTA";
import Footer from "@/components/landing/Footer";
import { getApiBaseUrl } from "@/lib/api";
import axios from "axios";

const API = getApiBaseUrl();
const DISPLAY_COUNT_OFFSET = 100;

function getDisplayWaitlistCount(count) {
  const signupCount = Number(count);
  return DISPLAY_COUNT_OFFSET + (Number.isFinite(signupCount) ? signupCount : 0);
}

export default function LandingPage() {
  const [waitlistCount, setWaitlistCount] = useState(100);
  const [submitted, setSubmitted] = useState(false);
  const [submittedRank, setSubmittedRank] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    const fetchCount = async () => {
      try {
        const res = await axios.get(`${API}/waitlist/count`);
        setWaitlistCount(getDisplayWaitlistCount(res.data.count));
      } catch (e) {
        console.warn("Unable to fetch waitlist count", e);
      }
    };
    fetchCount();
  }, []);

  useEffect(() => {
    let observer;
    let disposed = false;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (disposed) return;
        const sections = document.querySelectorAll("[data-reveal]");
        if (!sections.length) return;

        observer = new IntersectionObserver(
          (entries, obs) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              entry.target.classList.add("is-visible");
              obs.unobserve(entry.target);
            });
          },
          {
            threshold: 0.1,
            rootMargin: "0px 0px 10% 0px",
          }
        );

        sections.forEach((section) => observer.observe(section));
      });
    });

    return () => {
      disposed = true;
      observer?.disconnect();
    };
  }, []);

  const handleWaitlist = async (email) => {
    setLoading(true);
    setMessage("");
    try {
      const res = await axios.post(`${API}/waitlist`, { email });
      setMessage(res.data.message);
      setSubmitted(true);
      const rank = getDisplayWaitlistCount(res.data.count);
      setSubmittedRank(rank);
      setWaitlistCount(rank);
    } catch (e) {
      setMessage(e.response?.data?.detail || "Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen nm-page" data-testid="landing-page">
      <Navbar />
      
      {/* 1. Hero (Light) */}
      <HeroSection
        onSubmit={handleWaitlist}
        loading={loading}
        submitted={submitted}
        message={message}
        waitlistCount={waitlistCount}
        submittedRank={submittedRank}
      />
      
      {/* 2. AI Preview (Dark) */}
      <AIPreview />
      
      {/* 3. Protocol (Light) */}
      <SolutionSection />
      
      {/* 4. Comparison (Dark) */}
      <TheDifference />
      
      {/* 5. Problem (Light) */}
      <ProblemSection />

      {/* 6. Social Proof (Dark) */}
      <SocialProof />
      
      {/* 7. Early Access (Light) */}
      <EarlyAccessSection count={waitlistCount} />
      
      {/* 8. FAQs (Dark) */}
      <FAQ />
      
      {/* 9. Final CTA (Light) */}
      <FinalCTA
        onSubmit={handleWaitlist}
        loading={loading}
        submitted={submitted}
        message={message}
        submittedRank={submittedRank}
        waitlistCount={waitlistCount}
      />
      
      {/* 10. Footer (Dark) */}
      <Footer />
    </div>
  );
}
